import * as React from 'react'

import type { DashboardSdrComparisonResponse } from '@sdr-agend/admin-schemas'
import { formatPercentOrDash } from '@/lib/format'

/**
 * "Comparativo de carteiras por SDR" — Story 12.9 (Epic E12).
 *
 * Uma linha por SDR (ativas + desativadas com lead no período — AC5, ver
 * `dashboard-sdr-comparison.ts`), não por métrica — diferente de
 * `CampaignComparisonTable` (que transpõe métrica × campanha) porque aqui a
 * dimensão comparada É a entidade principal (cada SDR é uma linha inteira),
 * não uma quebra adicional de uma métrica já existente.
 */
export interface SdrComparisonTableProps {
  data: DashboardSdrComparisonResponse
  /**
   * Story 12.10 (Epic E12) — optional row-expansion support, added for the
   * new `/admin/carteiras` page (which reuses this exact table to show the
   * per-SDR summary, then expands a row to reveal that SDR's customer
   * list). All three props are optional and unused by the original
   * Dashboard.tsx call site — zero behavior change there (ADAPT, not a
   * rewrite, per Article IV).
   */
  expandedSdrId?: string | null
  onToggleRow?: (sdrId: string) => void
  renderExpanded?: (row: DashboardSdrComparisonResponse['rows'][number]) => React.ReactNode
}

export function SdrComparisonTable({
  data,
  expandedSdrId = null,
  onToggleRow,
  renderExpanded,
}: SdrComparisonTableProps): React.ReactElement {
  if (data.rows.length === 0) {
    return (
      <div
        className="flex h-24 items-center justify-center rounded-md border border-dashed text-sm text-muted-foreground"
        data-testid="sdr-comparison-table-empty"
      >
        Nenhuma SDR cadastrada.
      </div>
    )
  }

  return (
    <div className="overflow-x-auto" data-testid="sdr-comparison-table">
      <table className="w-full text-xs">
        <thead>
          <tr className="border-b text-left text-muted-foreground">
            <th className="pb-1.5 pr-3 font-medium">SDR</th>
            <th className="pb-1.5 pl-3 text-right font-medium">Leads recebidos</th>
            <th className="pb-1.5 pl-3 text-right font-medium">Conversas ativas</th>
            <th className="pb-1.5 pl-3 text-right font-medium">Reuniões marcadas</th>
            <th className="pb-1.5 pl-3 text-right font-medium">Taxa de resposta</th>
          </tr>
        </thead>
        <tbody>
          {data.rows.map((row) => (
            <React.Fragment key={row.sdrId}>
              <tr
                className={`border-b last:border-0${onToggleRow !== undefined ? ' cursor-pointer hover:bg-muted/40' : ''}`}
                data-testid={`sdr-comparison-row-${row.sdrId}`}
                onClick={onToggleRow !== undefined ? () => onToggleRow(row.sdrId) : undefined}
                // BACKLOG-LOW-12.10-F1 (@qa) — mesmo padrão de acessibilidade
                // já usado nas linhas clicáveis de Conversations.tsx
                // (role/tabIndex/onKeyDown), aqui com role="button" +
                // aria-expanded (é um toggle, não navegação).
                {...(onToggleRow !== undefined
                  ? {
                      role: 'button' as const,
                      tabIndex: 0,
                      'aria-expanded': expandedSdrId === row.sdrId,
                      'aria-label': `${expandedSdrId === row.sdrId ? 'Recolher' : 'Expandir'} carteira de ${row.sdrName}`,
                      onKeyDown: (e: React.KeyboardEvent<HTMLTableRowElement>) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault()
                          onToggleRow(row.sdrId)
                        }
                      },
                    }
                  : {})}
              >
                <td className="py-1.5 pr-3">
                  {row.sdrName}
                  {!row.sdrActive && (
                    <span className="ml-1.5 rounded-full bg-muted px-1.5 py-0.5 text-[10px] text-muted-foreground">
                      desativada
                    </span>
                  )}
                </td>
                <td className="py-1.5 pl-3 text-right tabular-nums">
                  {row.leadsReceived.toLocaleString('pt-BR')}
                </td>
                <td className="py-1.5 pl-3 text-right tabular-nums">
                  {row.activeConversations.toLocaleString('pt-BR')}
                </td>
                <td className="py-1.5 pl-3 text-right tabular-nums">
                  {row.meetingsScheduled.toLocaleString('pt-BR')}
                </td>
                <td className="py-1.5 pl-3 text-right tabular-nums">
                  {formatPercentOrDash(row.responseRate)}
                </td>
              </tr>
              {expandedSdrId === row.sdrId && renderExpanded !== undefined && (
                <tr data-testid={`sdr-comparison-row-${row.sdrId}-expanded`}>
                  <td colSpan={5} className="bg-muted/20 py-2 pl-3 pr-3">
                    {renderExpanded(row)}
                  </td>
                </tr>
              )}
            </React.Fragment>
          ))}
        </tbody>
      </table>
    </div>
  )
}

export default SdrComparisonTable
